/**
 * Compartilhamento de tela na chamada Twilio (estilo Google Meet).
 * A tela vai como LocalVideoTrack extra (`name: screen`); a câmera permanece.
 */

import { participantLabelFromIdentity } from "./videoParticipantLabel";

export const SCREEN_VIDEO_TRACK_NAME = "screen";
export const SCREEN_AUDIO_TRACK_NAME = "screen-audio";

export function isScreenShareSupported() {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getDisplayMedia === "function"
  );
}

function trackName(track) {
  return String(track?.name || "").trim().toLowerCase();
}

function displaySurfaceOf(track) {
  try {
    return String(track?.mediaStreamTrack?.getSettings?.()?.displaySurface || "");
  } catch {
    return "";
  }
}

export function isScreenShareTrack(track) {
  if (!track) return false;
  const name = trackName(track);
  if (
    name === SCREEN_VIDEO_TRACK_NAME ||
    name === "screenshare" ||
    name === "screen-share"
  ) {
    return track.kind !== "audio";
  }
  if (track.kind !== "video") return false;
  return Boolean(displaySurfaceOf(track));
}

export function isScreenShareAudioTrack(track) {
  if (!track || track.kind !== "audio") return false;
  const name = trackName(track);
  return name === SCREEN_AUDIO_TRACK_NAME || name === SCREEN_VIDEO_TRACK_NAME;
}

function isUserCancelledCapture(err) {
  const name = String(err?.name || "");
  const code = String(err?.code || "");
  return name === "AbortError" || name === "NotAllowedError" || code === "video.screen_cancelled";
}

function cancelledError(cause) {
  const err = new Error("Compartilhamento cancelado.");
  err.code = "video.screen_cancelled";
  if (cause) err.cause = cause;
  return err;
}

function resolveTwilioExport(Video, key) {
  return Video?.[key] || Video?.default?.[key] || null;
}

/**
 * Abre o seletor nativo (tela / janela / aba) e devolve tracks Twilio.
 * @param {typeof import("twilio-video")} Video
 */
export async function createLocalScreenShareTracks(Video) {
  if (!isScreenShareSupported()) {
    const err = new Error(
      "Compartilhar tela não está disponível neste navegador."
    );
    err.code = "video.screen_unsupported";
    throw err;
  }

  const LocalVideoTrack = resolveTwilioExport(Video, "LocalVideoTrack");
  const LocalAudioTrack = resolveTwilioExport(Video, "LocalAudioTrack");
  if (!LocalVideoTrack) {
    throw new Error("Não foi possível iniciar o compartilhamento de tela.");
  }

  const video = { frameRate: 15, width: 1920, height: 1080 };
  let stream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({ video, audio: true });
  } catch (e) {
    if (isUserCancelledCapture(e)) throw cancelledError(e);
    stream = await navigator.mediaDevices.getDisplayMedia({ video, audio: false });
  }

  const videoMedia = stream.getVideoTracks()[0];
  if (!videoMedia) {
    stream.getTracks().forEach((t) => {
      try {
        t.stop();
      } catch {
        /* ignore */
      }
    });
    throw new Error("Não foi possível capturar a tela.");
  }

  const videoTrack = new LocalVideoTrack(videoMedia, { name: SCREEN_VIDEO_TRACK_NAME });
  const audioMedia = stream.getAudioTracks()[0];
  const audioTrack =
    audioMedia && LocalAudioTrack
      ? new LocalAudioTrack(audioMedia, { name: SCREEN_AUDIO_TRACK_NAME })
      : null;

  return { videoTrack, audioTrack };
}

export function stopLocalScreenShareTracks({ videoTrack, audioTrack } = {}) {
  for (const t of [videoTrack, audioTrack]) {
    if (!t) continue;
    try {
      t.stop();
    } catch {
      /* ignore */
    }
    try {
      t.detach?.().forEach((el) => el.remove());
    } catch {
      /* ignore */
    }
  }
}

export function screenShareUnsupportedMessage() {
  return "Compartilhar tela não está disponível neste dispositivo. Use um computador com Chrome, Edge, Firefox ou Safari.";
}

export function friendlyScreenShareError(err) {
  if (!err || isUserCancelledCapture(err)) return "";
  if (err.code === "video.screen_unsupported") return screenShareUnsupportedMessage();
  const name = String(err.name || "");
  if (name === "NotFoundError") {
    return "Nenhuma tela ou janela foi encontrada para compartilhar.";
  }
  if (name === "NotReadableError") {
    return "Não foi possível ler a tela. Feche outros aplicativos que estejam usando a captura.";
  }
  return err.message || "Não foi possível compartilhar a tela.";
}

export function presenterLabelFromIdentity(identity, knownName) {
  return participantLabelFromIdentity(identity, knownName, "Participante");
}
